import { useDebounceValue } from '@dni-hooks/useDebounceValue'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { useApiCampaignCalculate } from '@hooks-api/fortune/useApiCampaignCalculate'
import { type CampaignCalculateRequest } from '@hooks-api/types/apiRequest'
import { useMemo } from 'react'
import { type FpB2bCartValues } from '../../types'
import { cartStoreMap, type PlatformType } from './store-map'

export const useFpB2bCartCalculate = (campaignId: number | null, campaignCode: string | undefined, platform: PlatformType) => {
  const { useCartSelector, useSyncCartSessionToStore, getCartValues } = cartStoreMap[platform]
  const { destructuredData: exhibitorSettings } = useApiCampaignActivityInfo(campaignCode)
  const { hasFinishedSyncCartSession } = useSyncCartSessionToStore({ campaignCode, campaignId })
  const billingCountry = useCartSelector(campaignId, state => state.merchant.billingCountry)
  const merchantIndex = useCartSelector(campaignId, state => state.merchant.index)
  const cartItems = useCartSelector(campaignId, state => state.cart.items)
  const manualInputCouponIds = useCartSelector(campaignId, state => state.coupon.manualInputCouponIds)
  const appliedShippingMethodId = useCartSelector(campaignId, state => state.shipping.appliedShippingMethodId)

  // 需要用 useMemo 來避免每次 render 時 value 的 reference 變更，會導致 useEffect 無窮迴圈
  const value = useMemo(() => {
    const cartValues = getCartValues(campaignId)
    const request: CampaignCalculateRequest<FpB2bCartValues> = {
      billing_country: billingCountry || exhibitorSettings?.region || null,
      campaign_code: campaignCode,
      merchant_id: merchantIndex,
      order_items: cartItems
        .map(cartItem => ({
          sku: cartItem.sku,
          quantity: cartItem.butler.isDeleted ? 0 : cartItem.quantity,
          is_addon: !!cartItem.is_addon,
          is_addon_v2: !!cartItem.is_addon_v2,
          project_code: cartItem.project_code,
          addon_setting_id: cartItem.addon_setting_id ?? null,
          addon_main_product_id: cartItem.addon_main_product_id ?? null,
        })),
      manual_input_coupon_ids: manualInputCouponIds,
      applied_shipping_method_id: appliedShippingMethodId,
      cart_values: cartValues,
    }

    return request
  }, [
    appliedShippingMethodId,
    billingCountry,
    campaignCode,
    campaignId,
    cartItems,
    exhibitorSettings?.region,
    getCartValues,
    manualInputCouponIds,
    merchantIndex,
  ])

  const defaultCampaignPayment = useMemo(() => {
    const payment = exhibitorSettings?.payment_methods.find(payment => payment.ordinal === 1)
    return { methodType: payment?.payment_option_type, serviceType: payment?.payment_service_config_type }
  }, [exhibitorSettings])

  const debouncedValue = useDebounceValue(value, 200)
  const { destructuredData: calculateResult, mutate, isLoading, isValidating } = useApiCampaignCalculate(
    hasFinishedSyncCartSession ? debouncedValue : undefined,
    {
      keepPreviousData: true,
    })

  // value !== debouncedValue 表示 cartValues 剛改、200ms debounce 視窗還沒過完
  // isValidating 表示 SWR 正在打 /calculate（含 keepPreviousData 的 stale-while-revalidate）
  // 兩者皆 false 時 calculateResult 才反映目前 cartValues，避免 user 在後端 cart_request_cache 同步前送出 checkout
  const isStale = value !== debouncedValue || isValidating

  return {
    calculateResult,
    mutate,
    isLoading,
    isStale,
    calculateValue: value,
    defaultCampaignPayment,
  }
}
